import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import { opportunitiesApi } from '../utils/api';

interface OpportunityDetailsData {
  match: {
    id: number;
    match_score: number;
    relevance_tier: string;
    reasoning?: string;
    risk_factors?: string[];
  };
  opportunity: {
    id: number;
    sam_id: string;
    title: string;
    description: string;
    agency: string;
    office?: string;
    naics_code: string;
    naics_description?: string;
    contract_value_min?: number;
    contract_value_max?: number;
    award_type?: string;
    set_aside_type?: string;
    post_date: string;
    response_deadline?: string;
    sam_url?: string;
  };
}

const OpportunityDetails: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const [details, setDetails] = useState<OpportunityDetailsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (matchId) {
      loadOpportunityDetails(parseInt(matchId));
    }
  }, [matchId]);

  const loadOpportunityDetails = async (id: number) => {
    try {
      const response = await opportunitiesApi.getMatchDetails(id);
      setDetails(response.data);
    } catch (error: any) {
      toast.error('Failed to load opportunity details');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return 'TBD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (!details) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Opportunity not found
          </h3>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {details.opportunity.title}
          </h1>
          <div className="mt-2">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                details.match.relevance_tier === 'high'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}
            >
              {details.match.relevance_tier === 'high' ? '🎯 High Priority' : '📋 Moderate'}
            </span>
            <span className="ml-2 text-sm text-gray-500">
              Match Score: {details.match.match_score}/100
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Opportunity Description
              </h3>
              <div className="prose prose-sm text-gray-700">
                <p>{details.opportunity.description}</p>
              </div>
            </div>

            {details.match.reasoning && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-blue-900 mb-4">
                  Why This Matches Your Profile
                </h3>
                <p className="text-blue-800">{details.match.reasoning}</p>
              </div>
            )}

            {details.match.risk_factors && details.match.risk_factors.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-yellow-900 mb-4">
                  Risk Factors to Consider
                </h3>
                <ul className="list-disc list-inside text-yellow-800 space-y-1">
                  {details.match.risk_factors.map((risk, index) => (
                    <li key={index}>{risk}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Opportunity Details
              </h3>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="font-medium text-gray-900">Agency</dt>
                  <dd className="text-gray-700">{details.opportunity.agency}</dd>
                </div>
                {details.opportunity.office && (
                  <div>
                    <dt className="font-medium text-gray-900">Office</dt>
                    <dd className="text-gray-700">{details.opportunity.office}</dd>
                  </div>
                )}
                <div>
                  <dt className="font-medium text-gray-900">NAICS Code</dt>
                  <dd className="text-gray-700">
                    {details.opportunity.naics_code}
                    {details.opportunity.naics_description && (
                      <div className="text-xs text-gray-500 mt-1">
                        {details.opportunity.naics_description}
                      </div>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-900">Contract Value</dt>
                  <dd className="text-gray-700">
                    {formatCurrency(details.opportunity.contract_value_min)} -{' '}
                    {formatCurrency(details.opportunity.contract_value_max)}
                  </dd>
                </div>
                {details.opportunity.set_aside_type && (
                  <div>
                    <dt className="font-medium text-gray-900">Set-Aside Type</dt>
                    <dd className="text-gray-700">{details.opportunity.set_aside_type}</dd>
                  </div>
                )}
                <div>
                  <dt className="font-medium text-gray-900">Posted Date</dt>
                  <dd className="text-gray-700">
                    {new Date(details.opportunity.post_date).toLocaleDateString()}
                  </dd>
                </div>
                {details.opportunity.response_deadline && (
                  <div>
                    <dt className="font-medium text-gray-900">Response Deadline</dt>
                    <dd className="text-gray-700">
                      {new Date(details.opportunity.response_deadline).toLocaleDateString()}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="font-medium text-gray-900">SAM ID</dt>
                  <dd className="text-gray-700 font-mono text-xs">
                    {details.opportunity.sam_id}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                {details.opportunity.sam_url && (
                  <a
                    href={details.opportunity.sam_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-center block"
                  >
                    View Full Details on SAM.gov
                  </a>
                )}
                <button className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500">
                  Mark as Pursuing
                </button>
                <button className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500">
                  Not Relevant
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OpportunityDetails;